import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { LayoutGrid, List, Plus, Search, Users, Archive, Bell, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Avatar } from "./Avatar";
import { TaskBoard } from "./TaskBoard";
import { TaskListe } from "./TaskListe";
import SakDrawer from "./SakDrawer";
import NySakModal from "./NySakModal";
import NyttMoteModal from "../moter/NyttMoteModal";
import PersonerModal from "./PersonerModal";
import VarslerModal from "./VarslerModal";
import { PRIORITETER, STATUS_LABEL, fristInfo, FRIST_KLASSE } from "./sakerUtils";
import "./saker.css";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

export default function SakerAdmin() {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [today, setToday] = useState("");
  const [laster, setLaster] = useState(true);
  const [visning, setVisning] = useState(() => localStorage.getItem("nas_saker_visning") || "tavle");
  const [sok, setSok] = useState("");
  const [filterPerson, setFilterPerson] = useState("");
  const [filterPri, setFilterPri] = useState(0);
  const [arkiv, setArkiv] = useState(false);
  const [aapenSakId, setAapenSakId] = useState(null);
  const [nySak, setNySak] = useState(null);
  const [visPersoner, setVisPersoner] = useState(false);
  const [visVarsler, setVisVarsler] = useState(false);
  const [moteForvalg, setMoteForvalg] = useState(null);
  const [aktor, setAktor] = useState(() => localStorage.getItem("nas_aktor") || "");

  const load = useCallback(async (vilArkiv) => {
    setLaster(true);
    try {
      const { data } = await api.get(`/admin/tasks${vilArkiv ? "?arkiv=1" : ""}`);
      setTasks(data.tasks || []);
      setMembers(data.members || []);
      setToday(data.today || "");
    } catch (e) {
      feil(e);
    } finally {
      setLaster(false);
    }
  }, []);

  useEffect(() => { load(arkiv); }, [load, arkiv]);

  useEffect(() => {
    if (!tasks.length) return;
    const p = new URLSearchParams(window.location.search).get("aapne");
    if (p && tasks.some((t) => t.id === p)) {
      setAapenSakId(p);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [tasks]);
  useEffect(() => { localStorage.setItem("nas_saker_visning", visning); }, [visning]);
  useEffect(() => { localStorage.setItem("nas_aktor", aktor); }, [aktor]);

  useEffect(() => {
    const tast = (e) => {
      if (e.key !== "n" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (t && /^(input|textarea|select)$/i.test(t.tagName)) return;
      if (aapenSakId || nySak || visPersoner || visVarsler) return;
      e.preventDefault();
      setNySak({ status: "inbox" });
    };
    window.addEventListener("keydown", tast);
    return () => window.removeEventListener("keydown", tast);
  }, [aapenSakId, nySak, visPersoner, visVarsler]);

  const aktorNavn = aktor || "Admin";

  const filtrerte = useMemo(() => {
    const q = sok.trim().toLowerCase();
    return tasks.filter((t) => {
      if (filterPerson && t.assigneeId !== filterPerson) return false;
      if (filterPri && (t.priority || 2) !== filterPri) return false;
      if (!q) return true;
      return (
        (t.title || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q) ||
        (t.labels || []).some((l) => l.toLowerCase().includes(q))
      );
    });
  }, [tasks, sok, filterPerson, filterPri]);

  const aapenSak = useMemo(() => tasks.find((t) => t.id === aapenSakId) || null, [tasks, aapenSakId]);

  const byttSak = (oppdatert) => setTasks((prev) => prev.map((t) => (t.id === oppdatert.id ? oppdatert : t)));

  const oppdaterSakRef = useRef(null);
  const oppdaterSak = useCallback(async (id, patch) => {
    const forrige = tasks.find((t) => t.id === id);
    if (!forrige) return;
    byttSak({ ...forrige, ...patch, updatedAt: new Date().toISOString() });
    try {
      const { data } = await api.put(`/admin/tasks/${id}`, { ...patch, actor: aktorNavn });
      if (data.task) {
        if ((patch.archived === true && !arkiv) || (patch.archived === false && arkiv)) {
          setTasks((prev) => prev.filter((t) => t.id !== id));
          setAapenSakId((cur) => (cur === id ? null : cur));
          toast.success(patch.archived ? "Saken ble arkivert" : "Saken ble gjenopprettet");
        } else {
          byttSak(data.task);
          if (patch.status && forrige.status !== patch.status) {
            const gammel = forrige.status;
            toast.success(patch.status === "done" ? "Saken er fullført" : `Flyttet til ${STATUS_LABEL[patch.status]}`, {
              action: { label: "Angre", onClick: () => oppdaterSakRef.current?.(id, { status: gammel }) },
            });
          }
        }
      }
    } catch (e) {
      byttSak(forrige);
      feil(e);
    }
  }, [tasks, aktorNavn, arkiv]);
  oppdaterSakRef.current = oppdaterSak;

  const opprettSak = async (payload) => {
    try {
      const { data } = await api.post("/admin/tasks", { ...payload, actor: aktorNavn });
      setTasks((prev) => [data.task, ...prev]);
      setNySak(null);
      toast.success("Sak opprettet");
      setAapenSakId(data.task.id);
    } catch (e) {
      feil(e);
    }
  };

  const slettSak = async (id) => {
    if (!window.confirm("Slette saken permanent? Dette kan ikke angres.")) return;
    try {
      await api.delete(`/admin/tasks/${id}`);
      setTasks((prev) => prev.filter((t) => t.id !== id));
      setAapenSakId(null);
      toast.success("Saken ble slettet");
    } catch (e) {
      feil(e);
    }
  };

  const kommenter = async (id, text, mentions = []) => {
    try {
      const { data } = await api.post(`/admin/tasks/${id}/comments`, { text, author: aktorNavn, mentions });
      setTasks((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, comments: [...(t.comments || []), data.comment], followers: data.followers ?? t.followers, updatedAt: data.comment.at }
            : t
        )
      );
    } catch (e) {
      feil(e);
      throw e;
    }
  };

  const settFolgere = async (id, memberIds) => {
    try {
      const { data } = await api.put(`/admin/tasks/${id}/followers`, { memberIds, actor: aktorNavn });
      setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, followers: data.followers } : t)));
    } catch (e) {
      feil(e);
    }
  };

  const endrePerson = async (id, form) => {
    try {
      const { data } = await api.patch(`/admin/task-members/${id}`, form);
      setMembers((prev) => prev.map((m) => (m.id === id ? data.member : m)));
      toast.success("Person oppdatert");
    } catch (e) { feil(e); throw e; }
  };

  const aapne = tasks.filter((t) => t.status !== "done").length;
  const harFilter = Boolean(sok.trim() || filterPerson || filterPri);
  const nullstillFiltre = () => { setSok(""); setFilterPerson(""); setFilterPri(0); };
  const megSelv = useMemo(() => members.find((m) => m.name === aktor) || null, [members, aktor]);

  const oppmerksomhet = useMemo(() => {
    if (arkiv || !today) return [];
    return tasks
      .filter((t) => t.status !== "done" && t.dueDate && t.dueDate <= today)
      .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));
  }, [tasks, today, arkiv]);
  const antForfalt = oppmerksomhet.filter((t) => t.dueDate < today).length;
  const antIdag = oppmerksomhet.length - antForfalt;

  const promoterDeloppgave = async (sakId, del) => {
    const sk = tasks.find((t) => t.id === sakId);
    if (!sk) return;
    try {
      const { data } = await api.post("/admin/tasks", {
        title: del.text,
        status: "inbox",
        priority: sk.priority || 2,
        assigneeId: del.assigneeId || null,
        dueDate: del.due || null,
        actor: aktorNavn,
      });
      if (data.task) setTasks((prev) => [data.task, ...prev]);
      await oppdaterSak(sakId, { subtasks: (sk.subtasks || []).filter((s) => s.id !== del.id) });
      toast.success("Deloppgaven ble en egen sak");
    } catch (e) {
      feil(e);
    }
  };

  return (
    <div data-testid="saker-admin" className="min-h-full">
      {/* Topp */}
      <header className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3 mb-5">
        <div>
          <p className="adm-eyebrow mb-1.5">Internt</p>
          <h1 className="adm-title">Saker</h1>
          <p className="text-xs text-neutral-500 mt-1" data-testid="saker-teller">
            {arkiv ? `${tasks.length} arkiverte saker` : `${aapne} åpne · ${tasks.length} totalt`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-[11.5px] text-neutral-500">
            <span className="hidden sm:inline">Du er</span>
            <select
              value={aktor}
              onChange={(e) => setAktor(e.target.value)}
              className="text-[12px] border border-[#E7E4DB] rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:border-neutral-400"
              data-testid="saker-aktor-select"
            >
              <option value="">Admin</option>
              {members.map((m) => (
                <option key={m.id} value={m.name}>{m.name}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setVisVarsler(true)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-neutral-700 border border-[#E7E4DB] bg-white rounded-lg px-2.5 sm:px-3 py-2 hover:bg-neutral-50 transition-colors"
            data-testid="saker-varsler-btn"
            title="E-postvarsler"
          >
            <Bell size={14} /> <span className="hidden sm:inline">Varsler</span>
          </button>
          <button
            type="button"
            onClick={() => setVisPersoner(true)}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-neutral-700 border border-[#E7E4DB] bg-white rounded-lg px-2.5 sm:px-3 py-2 hover:bg-neutral-50 transition-colors"
            data-testid="saker-personer-btn"
            title="Personer"
          >
            <Users size={14} /> <span className="hidden sm:inline">Personer</span>
          </button>
          <button
            type="button"
            onClick={() => setNySak({ status: "inbox" })}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium bg-neutral-900 text-white rounded-lg px-3.5 py-2 hover:bg-neutral-700 transition-colors"
            data-testid="saker-ny-sak-btn"
          >
            <Plus size={14} /> Ny sak
            <kbd className="hidden md:inline text-[9.5px] font-semibold bg-white/15 rounded px-1 py-px ml-0.5">N</kbd>
          </button>
        </div>
      </header>

      {/* Verktøyrad */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <div className="flex bg-white border border-[#E7E4DB] rounded-lg p-0.5">
          <button
            type="button"
            onClick={() => setVisning("tavle")}
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === "tavle" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
            data-testid="saker-view-tavle"
          >
            <LayoutGrid size={13} /> Tavle
          </button>
          <button
            type="button"
            onClick={() => setVisning("liste")}
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === "liste" ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
            data-testid="saker-view-liste"
          >
            <List size={13} /> Liste
          </button>
        </div>

        <div className="relative flex-1 min-w-[140px] sm:flex-none">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-300" />
          <input
            value={sok}
            onChange={(e) => setSok(e.target.value)}
            placeholder="Søk i saker …"
            className="text-[12.5px] border border-[#E7E4DB] bg-white rounded-lg pl-8 pr-3 py-2 w-full sm:w-[190px] sm:focus:w-[250px] focus:outline-none focus:border-neutral-400 transition-all placeholder:text-neutral-300"
            data-testid="saker-sok-input"
          />
        </div>

        {megSelv && (
          <button
            type="button"
            onClick={() => setFilterPerson((cur) => (cur === megSelv.id ? "" : megSelv.id))}
            className={`text-[12px] font-medium rounded-lg px-2.5 py-[7px] border transition-colors ${
              filterPerson === megSelv.id ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white text-neutral-600 hover:border-neutral-400"
            }`}
            title="Vis kun saker der du er ansvarlig"
            data-testid="saker-mine-chip"
          >
            Mine
          </button>
        )}

        {members.length > 0 && (
          <div className="flex items-center -space-x-1.5" data-testid="saker-person-filter">
            {members.slice(0, 8).map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setFilterPerson((cur) => (cur === m.id ? "" : m.id))}
                className={`rounded-full transition-all ${filterPerson === m.id ? "ring-2 ring-neutral-900 ring-offset-1 z-10" : filterPerson ? "opacity-40 hover:opacity-100" : "hover:z-10 hover:scale-110"}`}
                title={m.name}
                data-testid={`saker-filter-person-${m.id}`}
              >
                <Avatar person={m} size={26} className="border-2 border-[#FAF9F6]" />
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-1" data-testid="saker-pri-filter">
          {PRIORITETER.map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => setFilterPri((cur) => (cur === p.v ? 0 : p.v))}
              className={`bud-mono text-[11px] font-bold rounded-lg px-2 py-[7px] border transition-colors ${
                filterPri === p.v ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white hover:border-neutral-400"
              }`}
              style={filterPri === p.v ? {} : { color: p.farge }}
              title={p.label}
              data-testid={`saker-pri-chip-${p.v}`}
            >
              {p.kort}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => { setArkiv((v) => !v); setAapenSakId(null); }}
          className={`ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium rounded-lg px-2.5 py-2 border transition-colors ${arkiv ? "bg-neutral-900 text-white border-neutral-900" : "text-neutral-500 border-[#E7E4DB] bg-white hover:text-neutral-800"}`}
          data-testid="saker-arkiv-toggle"
        >
          <Archive size={13} /> Arkiv
        </button>
      </div>

      {harFilter && !laster && (
        <div className="flex items-center gap-2.5 mb-3" data-testid="saker-filter-status">
          <span className="bud-mono text-[11px] tabular-nums text-neutral-400">Viser {filtrerte.length} av {tasks.length}</span>
          <button
            type="button"
            onClick={nullstillFiltre}
            className="text-[11px] font-medium text-neutral-500 hover:text-neutral-900 underline underline-offset-2 transition-colors"
            data-testid="saker-filter-nullstill"
          >
            Nullstill
          </button>
        </div>
      )}

      {!laster && oppmerksomhet.length > 0 && (
        <div className="mb-6" data-testid="saker-oppmerksomhet">
          <div className="flex items-center gap-2 border-t-2 border-red-600/80 pt-2.5 mb-2.5">
            <AlertTriangle size={12} className="text-red-600" />
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-red-700">Krever oppmerksomhet</span>
            <span className="bud-mono text-[10.5px] tabular-nums text-neutral-400" data-testid="saker-oppmerksomhet-teller">
              {antForfalt > 0 ? `${antForfalt} forfalt` : ""}{antForfalt > 0 && antIdag > 0 ? " · " : ""}{antIdag > 0 ? `${antIdag} i dag` : ""}
            </span>
          </div>
          <div className="flex gap-2 overflow-x-auto sak-noscroll pb-1">
            {oppmerksomhet.map((t) => {
              const fi = fristInfo(t.dueDate, today);
              const ansv = members.find((m) => m.id === t.assigneeId);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setAapenSakId(t.id)}
                  className="shrink-0 max-w-[280px] flex items-center gap-2.5 bg-white border border-[#E7E4DB] rounded-[10px] px-3 py-2 text-left hover:border-neutral-400 transition-colors"
                  data-testid={`saker-oppmerksomhet-chip-${t.id}`}
                >
                  <span className={`bud-mono text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${FRIST_KLASSE[fi.tone]}`}>{fi.label}</span>
                  <span className="text-[12.5px] font-medium text-neutral-900 truncate">{t.title}</span>
                  {ansv && <Avatar person={ansv} size={18} />}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Innhold */}
      {laster ? (
        <div className="sak-tavle" data-testid="saker-laster" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="sak-kolonne pt-3 px-1">
              <div className="sak-skjelett h-3 w-24 rounded mb-4" />
              {[0, 1, 2].map((j) => (
                <div key={j} className="sak-skjelett h-[76px] rounded-[10px] mb-2" style={{ animationDelay: `${(i * 3 + j) * 80}ms` }} />
              ))}
            </div>
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-24 bg-white border border-dashed border-[#E7E4DB] rounded-2xl" data-testid="saker-tom">
          <p className="text-sm font-medium text-neutral-700">{arkiv ? "Arkivet er tomt" : "Ingen saker ennå"}</p>
          <p className="text-xs text-neutral-400 mt-1.5">
            {arkiv ? "Arkiverte saker havner her." : "Opprett den første saken — trykk N eller bruk knappen."}
          </p>
          {!arkiv && (
            <button
              type="button"
              onClick={() => setNySak({ status: "inbox" })}
              className="mt-5 text-[12.5px] font-medium bg-neutral-900 text-white rounded-lg px-4 py-2 hover:bg-neutral-700 transition-colors"
              data-testid="saker-tom-ny-sak"
            >
              + Ny sak
            </button>
          )}
        </div>
      ) : filtrerte.length === 0 ? (
        <div className="text-center py-20" data-testid="saker-ingen-treff">
          <p className="text-sm font-medium text-neutral-700">Ingen treff</p>
          <p className="text-xs text-neutral-400 mt-1.5">Ingen saker matcher filtrene dine.</p>
          <button
            type="button"
            onClick={nullstillFiltre}
            className="mt-4 text-[12px] font-medium text-neutral-600 border border-[#E7E4DB] bg-white rounded-lg px-3 py-1.5 hover:border-neutral-400 transition-colors"
            data-testid="saker-ingen-treff-nullstill"
          >
            Nullstill filtre
          </button>
        </div>
      ) : visning === "tavle" ? (
        <TaskBoard
          saker={filtrerte}
          members={members}
          today={today}
          onOpen={setAapenSakId}
          onDrop={(status, id) => {
            const t = tasks.find((x) => x.id === id);
            if (t && t.status !== status) oppdaterSak(id, { status });
          }}
          onQuickAdd={(status) => setNySak({ status })}
          onStatus={(id, status) => oppdaterSak(id, { status })}
        />
      ) : (
        <TaskListe saker={filtrerte} members={members} today={today} onOpen={setAapenSakId} onStatus={(id, status) => oppdaterSak(id, { status })} />
      )}

      {/* Overlays */}
      {aapenSak && (
        <SakDrawer
          sak={aapenSak}
          members={members}
          today={today}
          aktor={aktorNavn}
          onClose={() => setAapenSakId(null)}
          onUpdate={(patch) => oppdaterSak(aapenSak.id, patch)}
          onComment={(text, mentions) => kommenter(aapenSak.id, text, mentions)}
          onFollowers={(ids) => settFolgere(aapenSak.id, ids)}
          onDelete={() => slettSak(aapenSak.id)}
          onReplaceTask={byttSak}
          onPromote={(del) => promoterDeloppgave(aapenSak.id, del)}
          onMote={() => {
            const eposter = [aapenSak.assigneeId, ...(aapenSak.followers || [])]
              .map((id) => members.find((m) => m.id === id)?.email)
              .filter(Boolean);
            setMoteForvalg({ subject: aapenSak.title, attendees: [...new Set(eposter)], taskId: aapenSak.id });
          }}
        />
      )}
      {nySak && (
        <NySakModal
          forvalgtStatus={nySak.status}
          members={members}
          onLagre={opprettSak}
          onLukk={() => setNySak(null)}
        />
      )}
      {visPersoner && (
        <PersonerModal
          members={members}
          onEndre={endrePerson}
          onLukk={() => setVisPersoner(false)}
        />
      )}
      {visVarsler && <VarslerModal onLukk={() => setVisVarsler(false)} />}
      {moteForvalg && (
        <NyttMoteModal
          members={members}
          organizer=""
          forvalg={moteForvalg}
          onLagret={() => { setMoteForvalg(null); load(arkiv); }}
          onLukk={() => setMoteForvalg(null)}
        />
      )}
    </div>
  );
}
